import moment from "moment";
import { parentOrgId } from "../mockData/multitenancy";
import {
  previousMonthCost,
  previousMonthCostSum,
  currentMonthCost,
  restMonthsSums,
  currencyCode,
  topServicesRatioToCurrentCost,
  topAccountsRatioToCurrentCost,
  servicesList,
  regionList,
  topResourcesRatioToCurrentCost,
  resourceList,
  topRegionRatioToCurrentCost,
  generateRandomAWSResourceIds,
  resourceToServiceMap,
  generateServiceSpecificAWSResourceIds,
  secondGroupCostRatio,
  thirdGroupCostRatio,
  thirdLevelResourcesRatioToCurrentCost,
  secondLevelResourcesRatioToCurrentCost,
} from "../mockData/costDashboard/costDashboardMockData";
import { getAllAccounts } from "./accounts";
import {
  QUERY_PARAM,
  API_FILTER_KEYS,
} from "../../modules/cost/components/dashboard/utils/constants";

const createPreviousMonthObject = () => {
  const previousMonth = moment().subtract(1, "months");
  const daysInPreviousMonth = 30;
  const previousMonthObject = {};

  for (let day = 1; day <= daysInPreviousMonth; day++) {
    const currentDateFormatted = previousMonth.date(day).format("YYYY-MM-DD");
    const amount = previousMonthCost[day - 1];

    previousMonthObject[currentDateFormatted] = {
      Amount: amount,
      Unit: currencyCode,
    };
  }

  return previousMonthObject;
};

const createCurrentMonthObject = () => {
  const currentDate = moment();
  const currentDay = currentDate.date();

  const currentMonthObject = {};

  const daysInCurrentMonth = Math.min(currentDay, 30);

  for (let day = 1; day <= daysInCurrentMonth; day++) {
    const currentDateFormatted = currentDate.date(day).format("YYYY-MM-DD");
    const amount = currentMonthCost[day - 1];

    currentMonthObject[currentDateFormatted] = {
      Amount: amount,
      Unit: currencyCode,
    };
  }

  return currentMonthObject;
};

const currentMonthCostSumTillDate = () => {
  const currentDate = moment();
  const currentDay = currentDate.date();

  const daysInCurrentMonth = Math.min(currentDay, 30);

  const expensesTillDate = currentMonthCost.slice(0, daysInCurrentMonth);

  const sum = expensesTillDate.reduce((total, amount) => total + amount, 0);

  return sum;
};

const generateLastSixMonthsCost = () => {
  const startOfCurrentMonth = moment().startOf("month");
  const lastSixMonths = {};

  lastSixMonths[startOfCurrentMonth.format("YYYY-M-DD")] = {
    Amount: currentMonthCostSumTillDate(),
    Unit: currencyCode,
  };

  for (let i = 1; i < 6; i++) {
    const startDate = startOfCurrentMonth
      .clone()
      .subtract(i, "months")
      .startOf("month");
    const sum = i === 1 ? previousMonthCostSum : restMonthsSums[i - 2];

    lastSixMonths[startDate.format("YYYY-M-DD")] = {
      Amount: sum,
      Unit: currencyCode,
    };
  }

  return lastSixMonths;
};

const getLastUpdated = () => {
  const pastDateTime = moment().subtract(12, "hours"); //Always return 12 hours before now
  const formattedDateTime = pastDateTime.format("YYYY-MM-DD HH:mm:ss");
  return formattedDateTime;
};

const getTop5ServicesCost = () => {
  const currentMonthCostTD = currentMonthCostSumTillDate();
  const top5Services = servicesList.slice(0, 5);

  const startOfCurrentMonth = moment().startOf("month");

  const result = {};
  top5Services.forEach((service, index) => {
    const startDate = startOfCurrentMonth.format("YYYY-MM-DD");
    //For demo purposes we are setting each service to a certain % of the current month's cost
    const amount =
      (topServicesRatioToCurrentCost[index] / 100) * currentMonthCostTD;

    result[service] = {
      [startDate]: {
        Amount: amount,
        Unit: currencyCode,
      },
    };
  });

  return result;
};

const getTop5AccountsCost = () => {
  const accountsList = getAllAccounts();
  const currentMonthCostTD = currentMonthCostSumTillDate();

  const top5Accounts = accountsList.slice(0, 5);

  const startOfCurrentMonth = moment().startOf("month");

  const result = {};
  top5Accounts.forEach((account, index) => {
    const startDate = startOfCurrentMonth.format("YYYY-MM-DD");
    //For demo purposes we are setting each account to a certain % of the current month's cost
    const amount =
      (topAccountsRatioToCurrentCost[index] / 100) * currentMonthCostTD;

    result[account.AccountNumber] = {
      [startDate]: {
        Amount: amount,
        Unit: currencyCode,
      },
    };
  });

  return result;
};

export const costDashboardAggregate = () => {
  const previousMonthDailyCost = createPreviousMonthObject();
  const currentMonthDailyCost = createCurrentMonthObject();

  return {
    CustomerId: "1c83cc3b-be56-11e9-8c31-06e13bd26390",
    DailyCost: {
      ...previousMonthDailyCost,
      ...currentMonthDailyCost,
    },
    LastUpdatedOn: getLastUpdated(),
    MonthlyCost: generateLastSixMonthsCost(),
    OrganizationId: parentOrgId,
    TopAccountsCost: getTop5AccountsCost(),
    TopServicesCost: getTop5ServicesCost(),
  };
};

export const costDashboardLookup = () => {
  const accountsList = getAllAccounts();

  const top20Accounts = accountsList.slice(0, 20);
  const top20AccountsMap = {};

  top20Accounts.forEach((account) => {
    top20AccountsMap[account.AccountNumber] = account.AccountName;
  });

  return {
    Accounts: top20AccountsMap,
    CustomerId: "1c83cc3b-be56-11e9-8c31-06e13bd26390",
    OrganizationId: parentOrgId,
    Regions: JSON.stringify(regionList),
    Services: JSON.stringify(servicesList),
    UpdatedOn: getLastUpdated(),
  };
};

const getAccountsCostSummary = () => {
  const accountsList = getAllAccounts();
  const currentMonthCostTD = currentMonthCostSumTillDate();
  const totalCost = currentMonthCostTD + previousMonthCostSum;

  const result = [];
  accountsList.forEach((account, index) => {
    //For demo purposes we are setting each account to a certain % of the total cost
    const amount = (topAccountsRatioToCurrentCost[index] / 100) * totalCost;

    result.push({
      AccountNumber: account.AccountNumber,
      Cost: {
        Amount: amount,
        Unit: currencyCode,
      },
    });
  });

  return result;
};

const getServicesCostSummary = () => {
  const currentMonthCostTD = currentMonthCostSumTillDate();
  const totalCost = currentMonthCostTD + previousMonthCostSum;

  const result = [];
  servicesList.forEach((service, index) => {
    //For demo purposes we are setting each service to a certain % of the total cost
    const amount = (topServicesRatioToCurrentCost[index] / 100) * totalCost;

    result.push({
      ServiceName: service,
      Cost: {
        Amount: amount,
        Unit: currencyCode,
      },
    });
  });

  return result;
};

export const costDashboardSummary = (summaryType) => {
  if (summaryType === QUERY_PARAM.VALUE.BY_ACCOUNT) {
    const accountsCost = getAccountsCostSummary();
    return accountsCost;
  } else if (summaryType === QUERY_PARAM.VALUE.BY_SERVICE) {
    const servicesCost = getServicesCostSummary();
    return servicesCost;
  }
};

export const regionSummary = () => {
  const currentMonthCostTD = currentMonthCostSumTillDate();
  const top5Regions = regionList.slice(0, 5);
  const totalCost = currentMonthCostTD + previousMonthCostSum;

  const result = [];
  top5Regions.forEach((region, index) => {
    //For demo purposes we are setting each region to a certain % of the total cost
    const amount = (topRegionRatioToCurrentCost[index] / 100) * totalCost;

    result.push({
      Region: region,
      Cost: {
        Amount: amount,
        Unit: currencyCode,
      },
    });
  });

  return result;
};

export const resourceSummary = () => {
  const currentMonthCostTD = currentMonthCostSumTillDate();
  const top5Resources = resourceList.slice(0, 5);
  const accountsList = getAllAccounts();
  const topAccount = accountsList[0];
  const totalCost = currentMonthCostTD + previousMonthCostSum;

  const result = [];
  top5Resources.forEach((resourceId, index) => {
    //For demo purposes we are setting each resource to a certain % of the total cost
    const amount = (topResourcesRatioToCurrentCost[index] / 100) * totalCost;

    result.push({
      AccountNumber: topAccount.AccountNumber,
      ResourceId: resourceId,
      Cost: {
        Amount: amount,
        Unit: currencyCode,
      },
    });
  });

  return result;
};

const generateSpendTrendMap = ({ itemsCount, monthStartDates }) => {
  const [startOfCurrentMonth, startOfPreviousMonth] = monthStartDates;

  const generateKey = (prefix, month) => `${month}-${prefix}`;

  const accountsList = getAllAccounts();
  const topAccounts = accountsList.slice(0, itemsCount);
  const topRegions = regionList.slice(0, itemsCount);
  const topServices = servicesList.slice(0, itemsCount);

  const currentMonthCostTD = currentMonthCostSumTillDate();

  const trendTypeMap = {
    [QUERY_PARAM.VALUE.BY_ACCOUNT]: {
      key: API_FILTER_KEYS.ACCOUNT_NUMBER,
      list: topAccounts,
      ratio: topAccountsRatioToCurrentCost,
      [generateKey("sum", startOfCurrentMonth)]: currentMonthCostTD,
      [generateKey("sum", startOfPreviousMonth)]: previousMonthCostSum,
    },
    [QUERY_PARAM.VALUE.BY_SERVICE]: {
      key: API_FILTER_KEYS.SERVICE,
      list: topServices,
      ratio: topServicesRatioToCurrentCost,
      [generateKey("sum", startOfCurrentMonth)]: currentMonthCostTD,
      [generateKey("sum", startOfPreviousMonth)]: previousMonthCostSum,
    },

    [QUERY_PARAM.VALUE.BY_REGION]: {
      key: API_FILTER_KEYS.REGION,
      list: topRegions,
      ratio: topRegionRatioToCurrentCost,
      [generateKey("sum", startOfCurrentMonth)]: currentMonthCostTD,
      [generateKey("sum", startOfPreviousMonth)]: previousMonthCostSum,
    },
  };
  return trendTypeMap;
};

export const spendTrend = (type) => {
  const startOfPreviousMonth = moment()
    .subtract(1, "months")
    .startOf("month")
    .format("YYYY-MM-DD");
  const startOfCurrentMonth = moment().startOf("month").format("YYYY-MM-DD");
  const monthlyDates = [startOfCurrentMonth, startOfPreviousMonth];

  const trendTypeMap = generateSpendTrendMap({
    itemsCount: 14,
    monthStartDates: monthlyDates,
  });

  const costDetails = {};
  const costTypeList = trendTypeMap[type].list;
  const costTypeKey = trendTypeMap[type].key;

  monthlyDates.forEach((monthStart) => {
    const costTypeRatio = trendTypeMap[type].ratio;
    const costTypeSum = trendTypeMap[type][`${monthStart}-sum`];
    costDetails[monthStart] = costTypeList.map((costItem, index) => {
      return {
        [costTypeKey]:
          type === QUERY_PARAM.VALUE.BY_ACCOUNT
            ? costItem.AccountNumber
            : costItem,
        Amount: (costTypeRatio[index] / 100) * costTypeSum,
        Unit: currencyCode,
      };
    });
  });

  return {
    CostDetail: costDetails,
    Granularity: QUERY_PARAM.VALUE.MONTHLY,
  };
};

export const getServiceName = (resourceId) => {
  const resourceType = resourceId.split("-")[0];
  return resourceToServiceMap[resourceType];
};

const generateSpendBreakdownMap = ({ itemsCount, monthStartDates }) => {
  const [startOfCurrentMonth, startOfPreviousMonth] = monthStartDates;

  const generateKey = (prefix, month) => `${month}-${prefix}`;

  const accountsList = getAllAccounts();
  const topAccounts = accountsList.slice(0, itemsCount);
  const topRegions = regionList.slice(0, itemsCount);
  const topServices = servicesList.slice(0, itemsCount);

  const currentMonthCostTD = currentMonthCostSumTillDate();

  const trendTypeMap = {
    [QUERY_PARAM.VALUE.BY_ACCOUNT]: {
      key: API_FILTER_KEYS.ACCOUNT_NUMBER,
      list: topAccounts,
      ratio: topAccountsRatioToCurrentCost,
      [generateKey("sum", startOfCurrentMonth)]: currentMonthCostTD,
      [generateKey("sum", startOfPreviousMonth)]: previousMonthCostSum,
    },
    [QUERY_PARAM.VALUE.BY_SERVICE]: {
      key: API_FILTER_KEYS.SERVICE,
      list: topServices,
      ratio: topServicesRatioToCurrentCost,
      [generateKey("sum", startOfCurrentMonth)]: currentMonthCostTD,
      [generateKey("sum", startOfPreviousMonth)]: previousMonthCostSum,
    },

    [QUERY_PARAM.VALUE.BY_REGION]: {
      key: API_FILTER_KEYS.REGION,
      list: topRegions,
      ratio: topRegionRatioToCurrentCost,
      [generateKey("sum", startOfCurrentMonth)]: currentMonthCostTD,
      [generateKey("sum", startOfPreviousMonth)]: previousMonthCostSum,
    },
  };
  return trendTypeMap;
};

export const spendBreakdownSummary = (filterParams, offset) => {
  const { SummaryType, AccountNumbers, AWSServices, Regions } = filterParams;

  const isGroupedAt3Levels =
    (AccountNumbers && AWSServices) ||
    (AccountNumbers && Regions) ||
    (AWSServices && Regions);

  const isGroupedAt2Levels = AccountNumbers || AWSServices || Regions;

  const startOfPreviousMonth = moment()
    .subtract(1, "months")
    .startOf("month")
    .format("YYYY-MM-DD");
  const startOfCurrentMonth = moment().startOf("month").format("YYYY-MM-DD");

  const monthlyDates = [startOfCurrentMonth, startOfPreviousMonth];
  const spendBreakDownTypeMap = generateSpendBreakdownMap({
    itemsCount: 14,
    monthStartDates: monthlyDates,
  });

  const costDetails = {};
  const costTypeList = spendBreakDownTypeMap[SummaryType].list;

  let costTypeRatio = spendBreakDownTypeMap[SummaryType].ratio;

  if (isGroupedAt3Levels) {
    costTypeRatio = thirdGroupCostRatio;
  } else if (isGroupedAt2Levels) {
    costTypeRatio = secondGroupCostRatio;
  }

  costTypeList.forEach((costItem, index) => {
    const key =
      SummaryType === QUERY_PARAM.VALUE.BY_ACCOUNT
        ? costItem.AccountNumber
        : costItem;
    costDetails[key] = {
      Detail: [],
      TotalCost: 0,
    };

    monthlyDates.forEach((monthStart) => {
      const costTypeSum =
        spendBreakDownTypeMap[SummaryType][`${monthStart}-sum`];
      const monthlySum = (costTypeRatio[index] / 100) * costTypeSum;
      costDetails[key].Detail.push({
        Date: monthStart,
        Currency: currencyCode,
        Cost: monthlySum,
      });
      costDetails[key].TotalCost += monthlySum;
    });
  });

  return {
    Dimension: spendBreakDownTypeMap[SummaryType].key,
    Granularity: QUERY_PARAM.VALUE.MONTHLY,
    Items: costDetails,
    Offset: offset + 1,
  };
};

export const spendBreakdownResources = (filters, offset) => {
  const { AccountNumber, Service, Region } = filters;

  const isGroupedAt2Levels =
    (AccountNumber && Service) ||
    (AccountNumber && Region) ||
    (Service && Region);

  const isGroupedAt3Levels = AccountNumber && Service && Region;

  let costTypeRatio = topResourcesRatioToCurrentCost;

  if (isGroupedAt3Levels) {
    costTypeRatio = thirdLevelResourcesRatioToCurrentCost;
  } else if (isGroupedAt2Levels) {
    costTypeRatio = secondLevelResourcesRatioToCurrentCost;
  }

  const resourceIds = filters[API_FILTER_KEYS.SERVICE]
    ? generateServiceSpecificAWSResourceIds({
        idsCount: 20,
        service: filters[API_FILTER_KEYS.SERVICE],
      })
    : generateRandomAWSResourceIds({ idsCount: 20 });
  const accountsList = getAllAccounts();

  const currentMonthCostTD = currentMonthCostSumTillDate();
  const startOfPreviousMonth = moment()
    .subtract(1, "months")
    .startOf("month")
    .format("YYYY-MM-DD");
  const startOfCurrentMonth = moment().startOf("month").format("YYYY-MM-DD");

  const monthsList = [
    { date: startOfPreviousMonth, sum: currentMonthCostTD },
    { date: startOfCurrentMonth, sum: previousMonthCostSum },
  ];

  const resourceData = resourceIds.map((resourceId, index) => {
    const costData = monthsList.map((month) => {
      return {
        Currency: currencyCode,
        Date: month.date,
        Cost: (costTypeRatio[index] / 100) * month.sum,
      };
    });

    return {
      AccountId: filters[API_FILTER_KEYS.ACCOUNT_NUMBER]
        ? filters[API_FILTER_KEYS.ACCOUNT_NUMBER]
        : accountsList[0].AccountNumber,
      Service: filters[API_FILTER_KEYS.SERVICE]
        ? filters[API_FILTER_KEYS.SERVICE]
        : getServiceName(resourceId),
      Region: filters[API_FILTER_KEYS.ACCOUNT_NUMBER]
        ? filters[API_FILTER_KEYS.ACCOUNT_NUMBER]
        : regionList[0],
      ResourceId: resourceId,
      Cost: costData,
    };
  });

  return {
    Items: resourceData,
    Offset: offset + 1,
  };
};
